import { ATTACHMENT_FIELDS } from './constants';

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || ''));

export const isPendingNewBankRow = (bank) =>
  bank?.is_pending_new === true || Number(bank?.id) === 0;

export const isApprovedBankRow = (bank) => !isPendingNewBankRow(bank);

export const isPendingNewAddressRow = (address) =>
  address?.is_pending_new === true || Number(address?.id) === 0;

export const isApprovedAddressRow = (address) => !isPendingNewAddressRow(address);

export const hasPrimaryBank = (banks = []) =>
  (banks || []).some(
    (bank) =>
      !isPendingNewBankRow(bank) && String(bank.type || '').toUpperCase() === 'PRIMARY'
  );

export const getDefaultNewBankType = (banks = []) =>
  hasPrimaryBank(banks) ? 'OTHER' : 'PRIMARY';

export const isNewBankPendingEditRequest = (pendingEditRequest) => {
  if (!pendingEditRequest) return false;
  const { action, requested_details: requested } = pendingEditRequest;
  return action === 'add_bank' || (action === 'form_update' && Boolean(requested?.new_bank));
};

export const isVendorCreationApproval = (vendor) =>
  vendor?.request_type === 'vendor_creation';

export const isVendorEditApprovalWithNewBank = (vendor) =>
  vendor?.request_type === 'vendor_edit' &&
  Boolean(vendor?.pending_edit_request?.requested_details?.new_bank);

export const shouldShowBankFinanceTypeSelector = (vendor) => {
  if (!isVendorEditApprovalWithNewBank(vendor)) return false;
  return vendor.pending_edit_request?.bank_finance_approval?.show_type_selector === true;
};

export const getFinanceBankTypeDefault = (pendingEditRequest) => {
  const meta = pendingEditRequest?.bank_finance_approval;
  const fromApi = meta?.selected_type ?? meta?.default_type;
  if (fromApi) return String(fromApi).toUpperCase();
  if (meta?.has_existing_primary === true) return 'OTHER';
  return 'OTHER';
};

export const getApprovalBankDisplayType = (vendor, bank) => {
  if (isVendorCreationApproval(vendor)) return 'PRIMARY';
  return bank?.type;
};

export const formatBankTypeLabel = (type) => {
  const normalized = String(type || '').toUpperCase();
  if (normalized === 'PRIMARY') return 'Primary';
  if (normalized === 'OTHER') return 'Other';
  return type || '';
};

const appendNestedFields = (fd, prefix, fields) => {
  Object.entries(fields).forEach(([key, value]) => {
    if (value != null && value !== '') {
      fd.append(`${prefix}[${key}]`, value);
    }
  });
};

export const buildSubmitFormData = ({
  form,
  files,
  mode,
  vendorId,
  selectedAddressId,
  selectedBankId,
  selectedBankType,
  isNewAddress,
  isNewBank,
  existingAttachments = {},
  vendorBankDetails = [],
  tokenUserId,
}) => {
  const fd = new FormData();

  if (mode === 'edit' && vendorId) {
    fd.append('vendor_id', vendorId);
  }

  if (mode === 'create' && tokenUserId) {
    fd.append('token_user_id', tokenUserId);
  }

  const scalarFields = [
    'registration_type_of_gstin',
    'gst_no',
    'name_of_firm',
    'state_code',
    'geo_city_id',
    'geo_pincode_id',
    'vendor_address',
    'vendor_address2',
    'arn_no',
    'fax_no',
    'home_page',
    'contact_person_name',
    'contact_no',
    'email',
    'type_of_firm',
    'pan_no',
    'msme_reg_no',
    'msme_reg',
    'pf_reg_no',
    'pf_reg',
    'esic_reg_no',
    'esic_reg',
    'bank_name',
    'beneficiary_name',
    'currency_code',
    'ifsc_no',
    'bank_address',
    'bank_account_no',
    'swift_code',
    'iban',
    'status',
  ];

  scalarFields.forEach((key) => {
    if (form[key] != null && form[key] !== '') {
      fd.append(key, form[key]);
    }
  });

  if (form.geo_pincode_id) {
    fd.append('pincode', form.geo_pincode_id);
  }

  if (mode === 'edit') {
    if (selectedAddressId) fd.append('selected_address_id', selectedAddressId);
    if (selectedBankId) fd.append('selected_bank_id', selectedBankId);

    if (selectedBankId && !isNewBank && selectedBankType) {
      fd.append('bank_type', String(selectedBankType).toUpperCase());
    }

    if (isNewAddress) {
      fd.append('add_new_address', '1');
      appendNestedFields(fd, 'new_address', {
        name: form.contact_person_name,
        address: form.vendor_address,
        address2: form.vendor_address2,
        geo_state_id: form.state_code,
        geo_city_id: form.geo_city_id,
        geo_pincode_id: form.geo_pincode_id,
        gst_no: form.gst_no,
        arn_no: form.arn_no,
        phone_no: form.contact_no,
        email: form.email,
        fax_no: form.fax_no,
        home_page: form.home_page,
        contact: form.contact_person_name,
      });
    }

    if (isNewBank) {
      fd.append('add_new_bank', '1');
      const newBankFields = {
        name: form.bank_name,
        beneficiary_name: form.beneficiary_name,
        bank_account_no: form.bank_account_no,
        ifsc_code: form.ifsc_no,
        address: form.bank_address,
        currency_code: form.currency_code,
        swift_code: form.swift_code,
        iban: form.iban,
        geo_state_id: form.state_code,
        geo_city_id: form.geo_city_id,
        geo_pincode_id: form.geo_pincode_id,
      };
      if (hasPrimaryBank(vendorBankDetails)) {
        newBankFields.type = 'OTHER';
      }
      appendNestedFields(fd, 'new_bank', newBankFields);
    }

    if (form.remarks != null && String(form.remarks).trim() !== '') {
      fd.append('remarks', String(form.remarks).trim());
    }
  }

  ATTACHMENT_FIELDS.forEach(({ key }) => {
    const file = files[key];
    if (file instanceof File) {
      fd.append(key, file, file.name);
    } else if (
      mode === 'edit' &&
      existingAttachments[key] &&
      !(isNewBank && key === 'cancel_cheque_attachment')
    ) {
      fd.append(`${key}_old`, existingAttachments[key]);
    }
  });

  return fd;
};

export const vendorToForm = (vendor = {}, addressDetail = {}, bankDetail = {}) => ({
  registration_type_of_gstin: vendor.registration_type_of_gstin ?? '',
  gst_no: vendor.gst_no ?? '',
  name_of_firm: vendor.name_of_firm ?? '',
  state_code: String(vendor.state_code ?? addressDetail.geo_state_id ?? ''),
  geo_city_id: String(vendor.geo_city_id ?? addressDetail.geo_city_id ?? ''),
  geo_pincode_id: String(vendor.geo_pincode_id ?? addressDetail.geo_pincode_id ?? ''),
  vendor_address: vendor.vendor_address ?? addressDetail.address ?? '',
  vendor_address2: addressDetail.address2 ?? '',
  arn_no: addressDetail.arn_no ?? '',
  fax_no: addressDetail.fax_no ?? '',
  home_page: addressDetail.home_page ?? '',
  contact_person_name: vendor.contact_person_name ?? '',
  contact_no: vendor.contact_no ?? '',
  email: vendor.email ?? '',
  type_of_firm: vendor.type_of_firm ?? '',
  pan_no: vendor.pan_no ?? '',
  msme_reg_no: String(vendor.msme_reg_no ?? ''),
  msme_reg: vendor.msme_reg || '',
  pf_reg_no: String(vendor.pf_reg_no ?? ''),
  pf_reg: vendor.pf_reg ?? '',
  esic_reg_no: String(vendor.esic_reg_no ?? ''),
  esic_reg: vendor.esic_reg ?? '',
  bank_name: vendor.bank_name ?? bankDetail.name ?? '',
  beneficiary_name: bankDetail.beneficiary_name ?? '',
  currency_code: bankDetail.currency_code?.code ?? vendor.currency_code ?? 'INR',
  ifsc_no: vendor.ifsc_no ?? bankDetail.ifsc_code ?? '',
  bank_address: vendor.bank_address ?? bankDetail.address ?? '',
  bank_account_no: vendor.bank_account_no ?? bankDetail.bank_account_no ?? '',
  swift_code: bankDetail.swift_code ?? '',
  iban: bankDetail.iban ?? '',
  status: '2',
});

export const extractExistingAttachments = (vendor = {}) => ({
  pan_attachment: vendor.pan_attachment || '',
  gst_attachment: vendor.gst_attachment || '',
  cancel_cheque_attachment: vendor.cancel_cheque_attachment || '',
  msme_attatchment: vendor.msme_attatchment || '',
  declaration_for_attatchment: vendor.declaration_for_attatchment || '',
  pf_attatchement: vendor.pf_attatchement || '',
  esic_attatchemnt: vendor.esic_attatchemnt || '',
});

export const extractAttachmentViewUrls = (vendor = {}) => {
  const viewUrls = vendor.attachment_view_urls || {};
  const result = {};

  ATTACHMENT_FIELDS.forEach(({ key }) => {
    if (viewUrls[key]) {
      result[key] = viewUrls[key];
    } else if (isHttpUrl(vendor[key])) {
      result[key] = vendor[key];
    } else {
      result[key] = '';
    }
  });

  return result;
};
